/**
 * Formatting decimal money without binary floating point.
 *
 * <p>`MoneyValue.amount` arrives as a decimal string and is never mutated. The obvious
 * `Number(amount)` is what this file exists to avoid: IEEE-754 binary floating
 * point cannot hold `0.1`, so a four-figure kit priced at `4199.95` can render
 * a penny out, and a catalogue that is a penny out is a catalogue nobody
 * trusts. Display rounding uses integer minor units; only the
 * *presentation* (grouping separator, decimal separator, currency symbol and
 * its placement) comes from `Intl`, and it is read off a probe format rather
 * than applied to the value.
 *
 * <p>Currency travels with the amount because the platform runs in the UK and
 * in Turkey. No function here has a default currency, and none of them knows
 * what "the" locale is — both are arguments.
 */

export interface MoneyLike {
  amount: string;
  currency: string;
  taxInclusive?: boolean;
}

interface CurrencyShape {
  /** Text before the digits (a symbol, a space, a sign placeholder). */
  prefix: string;
  /** Text after the digits, for the currencies that suffix their symbol. */
  suffix: string;
  group: string;
  decimal: string;
  /** Digits after the decimal separator the currency conventionally shows. */
  fractionDigits: number;
}

const shapeCache = new Map<string, CurrencyShape>();

/**
 * What does `Intl` put around the digits for this locale/currency pair?
 *
 * <p>Formatted from the integer 1 — a value with no fraction to lose — purely
 * to read the parts back. The caller's own digits never go through `Intl`.
 */
function currencyShape(locale: string, currency: string): CurrencyShape {
  const key = `${locale} ${currency}`;
  const cached = shapeCache.get(key);
  if (cached) return cached;

  let shape: CurrencyShape = {
    prefix: `${currency} `,
    suffix: '',
    group: ',',
    decimal: '.',
    fractionDigits: 2,
  };
  try {
    const fmt = new Intl.NumberFormat(locale, { style: 'currency', currency });
    const parts = fmt.formatToParts(1);
    let seenDigits = false;
    let prefix = '';
    let suffix = '';
    let group = shape.group;
    let decimal = shape.decimal;
    for (const part of parts) {
      if (part.type === 'integer' || part.type === 'fraction') {
        seenDigits = true;
        continue;
      }
      if (part.type === 'group') {
        group = part.value;
        continue;
      }
      if (part.type === 'decimal') {
        decimal = part.value;
        continue;
      }
      if (seenDigits) suffix += part.value;
      else prefix += part.value;
    }
    shape = {
      prefix,
      suffix,
      group,
      decimal,
      fractionDigits: fmt.resolvedOptions().maximumFractionDigits ?? 2,
    };
  } catch {
    // An unknown currency code or an unsupported locale: fall through to the
    // conservative shape above rather than throwing on a catalogue page.
  }
  shapeCache.set(key, shape);
  return shape;
}

interface SplitAmount {
  negative: boolean;
  integer: string;
  fraction: string;
}

/** Split a decimal string into sign / integer digits / fraction digits. */
export function splitDecimal(amount: string | null | undefined): SplitAmount | null {
  const trimmed = (amount ?? '').trim();
  if (!/^[+-]?\d*(\.\d*)?$/.test(trimmed) || !/\d/.test(trimmed)) return null;
  const negative = trimmed.startsWith('-');
  const unsigned = trimmed.replace(/^[+-]/, '');
  const dot = unsigned.indexOf('.');
  const integer = (dot >= 0 ? unsigned.slice(0, dot) : unsigned).replace(/^0+(?=\d)/, '') || '0';
  const fraction = dot >= 0 ? unsigned.slice(dot + 1) : '';
  return { negative, integer, fraction };
}

/**
 * Round to the display scale, half away from zero, using decimal digits only.
 * Unit prices arrive at scale 4 even when a card shows the currency's ordinary
 * two places. Truncating those made the same price differ between home and
 * product pages. This changes presentation only; server amounts stay untouched.
 */
function roundForDisplay(split: SplitAmount, digits: number): SplitAmount {
  const kept = split.fraction.slice(0, digits).padEnd(digits, '0');
  let minor = BigInt(split.integer + kept);
  if ((split.fraction[digits] ?? '0') >= '5') minor += 1n;
  const rounded = minor.toString().padStart(digits + 1, '0');
  return {
    negative: split.negative,
    integer: digits ? rounded.slice(0, -digits) : rounded,
    fraction: digits ? rounded.slice(-digits) : '',
  };
}

function groupDigits(integer: string, separator: string): string {
  if (!separator) return integer;
  let out = '';
  for (let i = 0; i < integer.length; i += 1) {
    if (i > 0 && (integer.length - i) % 3 === 0) out += separator;
    out += integer[i];
  }
  return out;
}

/**
 * Render a money value for display.
 *
 * @param locale the active BCP 47 tag — from the site descriptor, never a
 *               literal
 * @param options.fractionDigits override the currency's conventional scale,
 *               for a unit price the backend sends at scale 4
 */
export function formatMoney(
  value: MoneyLike | null | undefined,
  locale: string,
  options: { fractionDigits?: number } = {},
): string | null {
  if (!value?.amount || !value.currency) return null;
  const split = splitDecimal(value.amount);
  if (!split) return null;

  const shape = currencyShape(locale, value.currency);
  const digits = options.fractionDigits ?? shape.fractionDigits;
  const rounded = roundForDisplay(split, digits);
  const body = rounded.fraction
    ? `${groupDigits(rounded.integer, shape.group)}${shape.decimal}${rounded.fraction}`
    : groupDigits(rounded.integer, shape.group);
  return `${split.negative ? '-' : ''}${shape.prefix}${body}${shape.suffix}`;
}

/**
 * The machine-readable amount for JSON-LD: the plain decimal string, sign
 * included, with no grouping and no symbol. Schema.org's `price` wants exactly
 * this, and re-deriving it from the display string is how a grouped, symbolised
 * figure ends up in a product feed.
 */
export function schemaAmount(value: MoneyLike | null | undefined): string | null {
  if (!value?.amount) return null;
  const split = splitDecimal(value.amount);
  if (!split) return null;
  const body = split.fraction ? `${split.integer}.${split.fraction}` : split.integer;
  return `${split.negative ? '-' : ''}${body}`;
}
